// Per-content-key diff over two version data maps (RES-561 compare feature).
//
// The backend content maps are {dataName: JSON string}; each diffable key's value
// parses to a Slate node array. Keys whose value isn't a Slate array (legacy
// Draft.js {blocks…}, scalar strings) are reported 'incomparable' — shown as a
// notice, never a crash. Callers normally pass the template's diffable keys; with
// no keys argument the union of both maps is compared.

import diffSlate from './diffSlate';
import { createDiffContext, markSubtree } from './annotate';
import { cloneNodes } from './normalise';

function parseValue(raw) {
	if (raw === undefined || raw === null)
		return { missing: true };
	let value = raw;
	if (typeof raw === 'string') {
		try {
			value = JSON.parse(raw);
		} catch (e) {
			return { incomparable: true };
		}
	}
	if (Array.isArray(value))
		return { nodes: value };
	return { incomparable: true };
}

function markWholeTree(nodes, prop, ctx) {
	const annotated = cloneNodes(nodes);
	const hunkId = ctx.newHunk(prop === 'diffAdded' ? 'added' : 'removed');
	annotated.forEach((node) => markSubtree(node, prop, hunkId));
	return { annotated, statuses: new Array(annotated.length).fill(prop === 'diffAdded' ? 'added' : 'removed') };
}

function diffKey(leftRaw, rightRaw, ctx) {
	// Identical raw values are unchanged whatever their format.
	if (leftRaw !== undefined && leftRaw === rightRaw) {
		const parsed = parseValue(leftRaw);
		const nodes = parsed.nodes || null;
		return {
			status: 'unchanged',
			leftAnnotated: nodes ? cloneNodes(nodes) : null,
			rightAnnotated: nodes ? cloneNodes(nodes) : null,
			blockStatuses: null,
		};
	}
	const left = parseValue(leftRaw);
	const right = parseValue(rightRaw);
	if (left.missing && right.missing)
		return { status: 'unchanged', leftAnnotated: null, rightAnnotated: null, blockStatuses: null };
	if (left.incomparable || right.incomparable)
		return { status: 'incomparable', leftAnnotated: null, rightAnnotated: null, blockStatuses: null };
	if (left.missing) {
		const { annotated, statuses } = markWholeTree(right.nodes, 'diffAdded', ctx);
		return { status: 'added', leftAnnotated: null, rightAnnotated: annotated, blockStatuses: { left: [], right: statuses } };
	}
	if (right.missing) {
		const { annotated, statuses } = markWholeTree(left.nodes, 'diffRemoved', ctx);
		return { status: 'removed', leftAnnotated: annotated, rightAnnotated: null, blockStatuses: { left: statuses, right: [] } };
	}
	const result = diffSlate(left.nodes, right.nodes, ctx);
	return {
		status: result.changed ? 'changed' : 'unchanged',
		leftAnnotated: result.leftAnnotated,
		rightAnnotated: result.rightAnnotated,
		blockStatuses: result.blockStatuses,
	};
}

// Returns {keys: {dataName: {status, leftAnnotated, rightAnnotated, blockStatuses}},
// hunks} — hunk ids are unique across all keys (shared context) and each hunk is
// stamped with the key it belongs to, for cross-key change navigation.
export default function diffContentMap(leftMap, rightMap, keys) {
	const left = leftMap || {};
	const right = rightMap || {};
	const allKeys = keys || [...new Set([...Object.keys(left), ...Object.keys(right)])];
	const ctx = createDiffContext();
	const results = {};
	allKeys.forEach((key) => {
		ctx.currentKey = key;
		results[key] = diffKey(left[key], right[key], ctx);
	});
	ctx.currentKey = undefined;
	return { keys: results, hunks: ctx.hunks };
}
